/* Statistics header: title, period presets, custom range, account picker,
   Gross/Net and Refresh. The range chip opens two date fields; the account
   picker is searchable (fleets run to hundreds of accounts). */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { C } from '../../ChatAssistant/compact/tokens';
import { font as f } from '../../ChatAssistant/compact/style';
import { Icon, Seg } from '../../ChatAssistant/compact/kit';
import { AccountPicker, type PickerAccount } from '../../AccountPicker';
import { chip, useCloseOnEscape } from '../../pickerKit';
import { PRESETS, shortDay, type MoneyMode } from './model';

export type { PickerAccount };

interface Props {
  subtitle: string;
  preset: number | null;
  onPreset: (days: number) => void;
  start: string;
  end: string;
  onRange: (start: string, end: string) => void;
  today: string;
  /** Fleet page only: the account picker. */
  accounts?: PickerAccount[];
  accountId?: string;
  onAccount?: (id: string | undefined) => void;
  mode: MoneyMode;
  onMode: (m: MoneyMode) => void;
  loading: boolean;
  onRefresh: () => void;
}

function RangeChip({ start, end, today, onRange }: { start: string; end: string; today: string; onRange: (s: string, e: string) => void }) {
  const { t } = useTranslation('statsUnified');
  const [open, setOpen] = useState(false);
  const [s, setS] = useState(start);
  const [e, setE] = useState(end);
  useEffect(() => { setS(start); setE(end); }, [start, end, open]);
  useCloseOnEscape(open, () => setOpen(false));
  const invalid = !s || !e || e < s;
  const field: React.CSSProperties = { height: 30, padding: '0 8px', background: C.input, border: `1px solid ${C.borderHi}`, borderRadius: 6, color: C.text, font: f(500, 12.5), colorScheme: 'dark' };
  return (
    <div style={{ position: 'relative' }}>
      <button type="button" style={chip} className="cpt-hover-border" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <Icon name="calendar" size={13} color={C.muted} />
        {start === end ? shortDay(start) : `${shortDay(start)} – ${shortDay(end)}`}
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 20 }} />
          <div role="dialog" style={{ position: 'absolute', top: 36, right: 0, zIndex: 21, width: 270, padding: 12, background: C.card, border: `1px solid ${C.borderHi}`, borderRadius: 8, boxShadow: '0 12px 32px rgba(0,0,0,.5)', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, font: f(600, 10), letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted }}>
                {t('range.from')}
                <input id="stx-range-from" type="date" value={s} max={e || today} onChange={(ev) => setS(ev.target.value)} style={field} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, font: f(600, 10), letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted }}>
                {t('range.to')}
                <input id="stx-range-to" type="date" value={e} min={s} max={today} onChange={(ev) => setE(ev.target.value)} style={field} />
              </label>
            </div>
            {invalid && <span style={{ font: f(400, 11.5), color: C.purplePale }}>{t('range.invalid')}</span>}
            <span style={{ font: f(400, 11), color: C.muted }}>{t('range.note')}</span>
            <button
              type="button"
              disabled={invalid}
              onClick={() => { onRange(s, e); setOpen(false); }}
              style={{ height: 28, border: 0, borderRadius: 6, background: C.purple, color: '#fff', font: f(600, 12), cursor: invalid ? 'not-allowed' : 'pointer', opacity: invalid ? 0.5 : 1 }}
            >
              {t('range.apply')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export function Header(p: Props) {
  const { t } = useTranslation('statsUnified');
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: '1 1 280px', minWidth: 0 }}>
        <span style={{ font: f(700, 18, 1.2), color: C.text }}>{t('title')}</span>
        <span style={{ font: f(400, 12), color: C.sub }}>{p.subtitle}</span>
      </div>
      <Seg
        size="md"
        value={p.preset}
        onChange={p.onPreset}
        options={PRESETS.map((d) => ({ id: d, label: d === 1 ? t('presets.today') : t('presets.days', { count: d }) }))}
      />
      <RangeChip start={p.start} end={p.end} today={p.today} onRange={p.onRange} />
      {p.accounts && p.onAccount && <AccountPicker accounts={p.accounts} value={p.accountId} onChange={p.onAccount} />}
      <Seg
        size="md"
        value={p.mode}
        onChange={p.onMode}
        options={[
          { id: 'gross', label: t('money.gross'), title: t('money.grossTip') },
          { id: 'net', label: t('money.net'), title: t('money.netTip') },
        ]}
      />
      <button type="button" title={t('refresh')} onClick={p.onRefresh} disabled={p.loading} className={`cpt-hover-border${p.loading ? ' om-busy' : ''}`} style={{ width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.input, border: `1px solid ${C.borderHi}`, borderRadius: 6, color: C.sub, cursor: 'pointer' }}>
        <Icon name="refresh-cw" size={13} />
      </button>
    </div>
  );
}
